import React, { useEffect, useState, useContext, useRef } from 'react';
import { useNavigate } from 'react-router';
import axios from 'axios';
import ls from 'localstorage-slim';
import { toast } from 'react-toastify';
import { FaRegTrashAlt } from 'react-icons/fa';
import { Icon } from '@rsuite/icons';
import { useTranslation } from 'react-i18next';

import DataContext from '../context/InvoiceData';
import { refreshToken, getETAToken, updateUserLastLogin } from '../helpers';
import Loading from '../GeneralComponents/Loading';

function InvoiceLines() {
  const {
    step,
    setStep,
    documentType,
    documentTypeVersion,
    setDocumentType,
    setDocumentTypeVersion,
    setInvoiceSubType,
  } = useContext(DataContext);

  let navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [invoicelines, setInvoicelines] = useState([]);
  const [taxlines, setTaxLines] = useState([]);
  const [itemCodeData, setItemCodeData] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [isUpdated, setIsUpdated] = useState(false);

  // update states
  const [updatedInvoiceline, setUpdatedInvoiceline] = useState('');

  // from database
  const [measureUnits, setMeasureUnits] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [taxables, setTaxables] = useState([]);
  const [taxSubtypes, setTaxSubtypes] = useState([]);
  const [itemCodes, setItemCodes] = useState([]);
  const [invoiceLinesList, setInvoiceLinesList] = useState([]);
  const [invoiceLinesListLength, setInvoiceLinesListLength] = useState(0);

  // calculation utilities
  const [amountEGP, setAmountEGP] = useState(0);
  const [quantity, setQuantity] = useState(0);
  const [discountRate, setDiscountRate] = useState(0);
  let [taxRate, setTaxRate] = useState(0);
  const [nonTaxableDiscount, setNonTaxableDiscount] = useState(0);
  const [valueDifference, setValueDiffernce] = useState(0);
  const [withholdTaxFactor, setWithholdTaxFactor] = useState(1);

  // calculation updated fields utilities
  const [updatedAmountEGP, setUpdatedAmountEGP] = useState(0);
  const [updatedQuantity, setUpdatedQuantity] = useState(0);
  const [updatedDiscountRate, setUpdatedDiscountRate] = useState(0);
  const [updatedTaxRate, setUpdatedTaxRate] = useState(0);
  const [updatedNonTaxableDiscount, setUpdatedNonTaxableDiscount] = useState(0);
  const [updatedValueDifference, setUpdatedValueDiffernce] = useState(0);

  const invoiceId = +ls.get('id');
  const subDomain = ls.get('subDomain');

  const refForm = useRef(null);

  const resetNumberInput = () => {
    setAmountEGP(0);
    setQuantity(0);
    setDiscountRate(0);
    setTaxRate(0);
    setNonTaxableDiscount(0);
    setValueDiffernce(0);
    setWithholdTaxFactor(1);
  };

  const resetUpdatedNumberInput = () => {
    setUpdatedAmountEGP(0);
    setUpdatedQuantity(0);
    setUpdatedDiscountRate(0);
    setUpdatedTaxRate(0);
    setUpdatedNonTaxableDiscount(0);
    setUpdatedValueDiffernce(0);
  };

  const handleChangeInvoiceLine = event => {
    let { name, value } = event.target;
    let numberFieldNamesArray = [
      'amount_egp',
      'quantity',
      'discount_rate',
      'tax_rate',
      'total_taxable_fees',
      'value_difference',
      'non_taxable_discount',
      'tax_type',
      'tax_subtype',
      'unit_type',
      'currency',
    ];

    if (numberFieldNamesArray.includes(name)) {
      value = +value;
    }

    isUpdateMode
      ? setUpdatedInvoiceline({ ...updatedInvoiceline, [name]: value })
      : setInvoicelines({ ...invoicelines, [name]: value });
  };

  const handleTaxType = event => {
    const taxSubtypesCopy = [...taxSubtypes];
    const selectedFilteredSubtypes = taxSubtypesCopy.filter(
      sub => sub.taxtype_reference === +event.target.value
    );
    setTaxLines(selectedFilteredSubtypes);
  };

  const handleSaveInvoiceline = event => {
    event.preventDefault();
    const saveInvoicelineToast = toast.loading('Please wait...');

    let sales_total = setSalesTotal();
    let discount_amount = setDiscountAmount();
    let tax_amount = setTaxAmount();
    let net_total = setNetAmount();
    let total = setTotal();

    let invoiceLinesData = {
      ...invoicelines,
      invoice: invoiceId,
      sales_total,
      discount_amount,
      tax_amount,
      net_total,
      total,
    };

    axios
      .post(`${subDomain}${process.env.REACT_APP_DJANGO_BASEURL}/invoice_lines/`, invoiceLinesData)
      .then(response => {
        toast.update(saveInvoicelineToast, {
          render: 'InvoiceLine Added successfully',
          type: 'success',
          isLoading: false,
          autoClose: 1000,
          theme: 'colored',
        });
        setInvoiceLinesListLength(invoiceLinesList.length + 1);
      })
      .then(() => {
        refForm.current.reset();
        resetNumberInput();
        setInvoicelines([]);
        setItemCodeData('');
      })
      .catch(error => {
        return toast.update(saveInvoicelineToast, {
          render: `${Object.keys(error.response.data)[0]}: ${
            Object.values(error.response.data)[0]
          }`,
          type: 'error',
          isLoading: false,
          autoClose: 1000,
          theme: 'colored',
        });
      });
  };

  const handleUpdateInvoiceline = event => {
    event.preventDefault();

    let invoicelineId = ls.get('invoiceline');
    const updateInvoicelineToast = toast.loading('Please wait...');

    let sales_total = setUpdatedSalesTotal();
    let discount_amount = setUpdatedDiscountAmount();
    let tax_amount = setUpdatedTaxAmount();
    let net_total = setUpdatedNetAmount();
    let total = setUpdatedTotal();

    let updatedInvoiceLinesData = {
      ...updatedInvoiceline,
      sales_total,
      discount_amount,
      tax_amount,
      net_total,
      total,
    };

    axios
      .put(
        `${subDomain}${process.env.REACT_APP_DJANGO_BASEURL}/invoice_lines/${invoicelineId}`,
        updatedInvoiceLinesData
      )
      .then(response => {
        toast.update(updateInvoicelineToast, {
          render: 'InvoiceLine Updated successfully',
          type: 'success',
          isLoading: false,
          autoClose: 1000,
          theme: 'colored',
        });
      })
      .then(() => {
        refForm.current.reset();
        resetUpdatedNumberInput();
        setUpdatedInvoiceline([]);
        setItemCodeData('');
        setIsUpdated(!isUpdated);
      })
      .catch(error => {
        return toast.update(updateInvoicelineToast, {
          render: `${Object.keys(error.response.data)[0]}: ${
            Object.values(error.response.data)[0]
          }`,
          type: 'error',
          isLoading: false,
          autoClose: 1000,
          theme: 'colored',
        });
      });
  };

  const handleGetInvoiceLine = id => {
    axios
      .get(`${subDomain}${process.env.REACT_APP_DJANGO_BASEURL}/invoice_lines/${id}`)
      .then(response => {
        let {
          amount_egp,
          quantity,
          discount_rate,
          tax_rate,
          value_difference,
          non_taxable_discount,
        } = response.data;
        setIsUpdateMode(true);

        setUpdatedAmountEGP(amount_egp);
        setUpdatedQuantity(quantity);
        setUpdatedDiscountRate(discount_rate);
        setUpdatedTaxRate(tax_rate);
        setUpdatedValueDiffernce(value_difference);
        setUpdatedNonTaxableDiscount(non_taxable_discount);

        setUpdatedInvoiceline(response.data);
      })

      .catch(error => {
        console.log(error);
      });
  };

  // blur events
  const handleBlurQuantity = event => {
    isUpdateMode ? setUpdatedQuantity(+event.target.value) : setQuantity(+event.target.value);
  };

  const handleBlurAmountEGP = event => {
    isUpdateMode ? setUpdatedAmountEGP(+event.target.value) : setAmountEGP(+event.target.value);
  };

  const handleBlurDiscountRate = event => {
    isUpdateMode
      ? setUpdatedDiscountRate(+event.target.value)
      : setDiscountRate(+event.target.value);
  };

  const handleBlurTaxRate = event => {
    invoicelines.tax_type === 4 ? setWithholdTaxFactor(-1) : setWithholdTaxFactor(1);

    isUpdateMode ? setUpdatedTaxRate(+event.target.value) : setTaxRate(+event.target.value);
  };

  const handleBlurItemsDiscount = event => {
    isUpdateMode
      ? setUpdatedNonTaxableDiscount(+event.target.value)
      : setNonTaxableDiscount(+event.target.value);
  };

  const handleBlurValueDifference = event => {
    isUpdateMode
      ? setUpdatedValueDiffernce(+event.target.value)
      : setValueDiffernce(+event.target.value);
  };

  // controlled fields
  const setSalesTotal = () => {
    const sales_total = +(amountEGP * quantity).toFixed(5);

    return sales_total;
  };

  const setDiscountAmount = () => {
    const discount_amount = +((amountEGP * quantity * discountRate) / 100).toFixed(5);

    return discount_amount;
  };

  const setTaxAmount = () => {
    if (invoicelines.tax_type === 4) {
      setWithholdTaxFactor(-1);
    }
    const sales_total = amountEGP * quantity;
    const discount_amount = (sales_total * discountRate) / 100;
    const value_difference_cut = (valueDifference * withholdTaxFactor * taxRate) / 100;
    const net_total = sales_total - discount_amount;
    const tax_amount = +(
      ((withholdTaxFactor * taxRate) / 100) * net_total +
      value_difference_cut
    ).toFixed(5);

    return tax_amount;
  };

  const setNetAmount = () => {
    const sales_total = amountEGP * quantity;
    const discount_amount = (sales_total * discountRate) / 100;

    const net_total = +(sales_total - discount_amount).toFixed(5);
    return net_total;
  };

  const setTotal = () => {
    const sales_total = amountEGP * quantity;
    const discount_amount = (sales_total * discountRate) / 100;
    const value_difference_cut = (valueDifference * withholdTaxFactor * taxRate) / 100;
    const net_total = sales_total - discount_amount;
    const tax_amount = ((withholdTaxFactor * taxRate) / 100) * net_total + value_difference_cut;

    const total = +(net_total + tax_amount - nonTaxableDiscount).toFixed(5);
    return total;
  };

  // controlled updated fields
  const setUpdatedSalesTotal = () => {
    const sales_total = +(updatedAmountEGP * updatedQuantity).toFixed(5);

    return sales_total;
  };

  const setUpdatedDiscountAmount = () => {
    const discount_amount = +(
      (updatedAmountEGP * updatedQuantity * updatedDiscountRate) /
      100
    ).toFixed(5);

    return discount_amount;
  };

  const setUpdatedTaxAmount = () => {
    const sales_total = updatedAmountEGP * updatedQuantity;
    const discount_amount = (sales_total * updatedDiscountRate) / 100;
    const value_difference_cut = (updatedValueDifference * updatedTaxRate) / 100;
    const net_total = sales_total - discount_amount;
    const tax_amount = +((updatedTaxRate / 100) * net_total + value_difference_cut).toFixed(5);

    return tax_amount;
  };

  const setUpdatedNetAmount = () => {
    const sales_total = updatedAmountEGP * updatedQuantity;
    const discount_amount = (sales_total * updatedDiscountRate) / 100;

    const net_total = +(sales_total - discount_amount).toFixed(5);
    return net_total;
  };

  const setUpdatedTotal = () => {
    const sales_total = updatedAmountEGP * updatedQuantity;
    const discount_amount = (sales_total * updatedDiscountRate) / 100;
    const value_difference_cut = (updatedValueDifference * updatedTaxRate) / 100;
    const net_total = sales_total - discount_amount;
    const tax_amount = (updatedTaxRate / 100) * net_total + value_difference_cut;

    const total = +(net_total + tax_amount - updatedNonTaxableDiscount).toFixed(5);
    return total;
  };

  const onChange = nextStep => {
    setStep(nextStep < 0 ? 0 : nextStep > 5 ? 5 : nextStep);
  };

  const handleClose = () => {
    setStep(0);
    setDocumentTypeVersion('');
    setDocumentType('');
    setInvoiceSubType('');
    ls.remove('id');
    navigate('../../document/new');
  };

  const handleContinue = () => {
    axios
      .put(`${subDomain}${process.env.REACT_APP_DJANGO_BASEURL}/invoices/${invoiceId}`, {
        status: 'ready',
      })
      .then(response => {
        onChange(step + 1);
      })
      .catch(error => {
        console.log(error);
      });
  };

  const handlePrevious = () => {
    onChange(step - 1);
  };

  const handleSelectItemCode = event => {
    const itemCodesCopy = [...itemCodes];
    const selectedItemCode = itemCodesCopy.find(
      itemCode => itemCode.codeLookupValue === event.target.value
    );
    setItemCodeData(selectedItemCode);
    isUpdateMode
      ? setUpdatedInvoiceline({
          ...updatedInvoiceline,
          description: selectedItemCode.codeDescriptionPrimaryLang,
          internal_code: selectedItemCode.codeLookupValue.split('-').at(-1),
        })
      : setInvoicelines({
          ...invoicelines,
          description: selectedItemCode.codeDescriptionPrimaryLang,
          internal_code: selectedItemCode.codeLookupValue.split('-').at(-1),
        });
  };

  const handleDeleteInvoiceline = id => {
    axios
      .delete(`${subDomain}${process.env.REACT_APP_DJANGO_BASEURL}/invoice_lines/${id}`)
      .then(response => {
        setInvoiceLinesListLength(invoiceLinesList.length - 1);
      })
      .catch(error => {
        console.log(error);
      });
  };

  useEffect(() => {
    async function fetchData() {
      try {
        if (!ls.get('access')) {
          await refreshToken();
          await updateUserLastLogin();
        }
        if (!ls.get('ETAToken')) await getETAToken();

        let response = await axios.get(
          `${subDomain}${process.env.REACT_APP_DJANGO_BASEURL}/erp/all_published_codes`,
          {
            params: {
              accessToken: ls.get('ETAToken'),
              codeType: 'EGS',
              taxpayer: ls.get('taxpayer_id'),
            },
          }
        );

        setItemCodes(response.data.result);
        setIsLoading(false);
      } catch (error) {
        if (error.response) {
          // Request made but the server responded with an error
          console.log(error.response.data);
        } else if (error.request) {
          // Request made but no response is received from the server.
          console.log(error.request);
        } else {
          // Error occured while setting up the request
          console.log('Error', error.message);
        }
      }
    }

    fetchData();
  }, []);

  useEffect(() => {
    const endpoints = [
      `${subDomain}${process.env.REACT_APP_DJANGO_BASEURL}/measure_units`,
      `${subDomain}${process.env.REACT_APP_DJANGO_BASEURL}/currencies`,
      `${subDomain}${process.env.REACT_APP_DJANGO_BASEURL}/taxables`,
      `${subDomain}${process.env.REACT_APP_DJANGO_BASEURL}/tax_subtypes`,
    ];

    Promise.all(endpoints.map(endpoint => axios.get(endpoint))).then(
      ([
        { data: measure_units },
        { data: currencies },
        { data: taxables },
        { data: tax_subtypes },
      ]) => {
        setMeasureUnits(measure_units);
        setCurrencies(currencies.filter(currency => currency.is_active === true));
        setTaxables(taxables);
        setTaxSubtypes(tax_subtypes);
      }
    );
  }, []);

  useEffect(() => {
    refreshToken();
    axios
      .get(`${subDomain}${process.env.REACT_APP_DJANGO_BASEURL}/erp/invoice_related_invoicelines`, {
        params: {
          invoiceId,
        },
      })
      .then(response => {
        setInvoiceLinesList(response.data);
        setInvoiceLinesListLength(response.data.length);
      })
      .catch(error => {
        console.log(error);
      });
  }, [invoiceLinesListLength, isUpdated]);

  return isLoading ? (
    <div style={{ height: '75vh' }}>
      <Loading />
    </div>
  ) : (
    <>
      <div className={`${i18n.language === 'ar' && 'text-end'} card border-0 shadow m-3`}>
        <div className='card-body body m-5'>
          <span className='p-2 fw-bold fs-5'>{`${documentType} | ${documentTypeVersion}`}</span>
          <div
            className={`${
              i18n.language === 'en' ? 'row mx-0' : 'd-flex flex-row-reverse'
            } my-3 p-2`}>
            <span
              className='subtitle text-decoration-underline pb-2'
              style={{ textUnderlineOffset: '10px' }}>
              <strong className='fs-5'>{t('stepper.invoiceLines.title')}</strong>
            </span>
          </div>
          <div
            className={`${
              i18n.language === 'en' ? 'justify-content-end' : 'justify-content-start'
            } row my-1`}>
            <button
              onClick={() => {
                setUpdatedInvoiceline([]);
                setIsUpdateMode(false);
              }}
              className='button text-nowrap'
              type='button'
              data-bs-toggle='offcanvas'
              data-bs-target='#offcanvasExample'
              aria-controls='offcanvasExample'>
              {t('stepper.invoiceLines.addButton')}
            </button>
          </div>
          <table className='table text-center' dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
            <thead
              style={{
                verticalAlign: 'middle',
              }}>
              <tr>
                <th scope='col'>{t('stepper.summary.lineItems.table.codeName')}</th>
                <th scope='col'>{t('stepper.summary.lineItems.table.itemCode')}</th>
                <th scope='col'>{t('stepper.summary.lineItems.table.description')}</th>
                <th scope='col'>{t('stepper.summary.lineItems.table.quantity')}</th>
                <th scope='col'>{t('stepper.summary.lineItems.table.unitPrice')}</th>
                <th scope='col'>{t('stepper.summary.lineItems.table.total')}</th>
                <th scope='col'></th>
              </tr>
            </thead>
            <tbody>
              {invoiceLinesList?.map(invoiceLine => (
                <tr key={invoiceLine.id}>
                  <td>{invoiceLine.item_type}</td>
                  <td
                    onClick={() => {
                      ls.set('invoiceline', invoiceLine.id);
                      setInvoicelines([]);
                      handleGetInvoiceLine(invoiceLine.id);
                    }}
                    data-bs-toggle='offcanvas'
                    data-bs-target='#offcanvasExample'
                    aria-controls='offcanvasExample'>
                    {invoiceLine.item_code}
                  </td>
                  <td>{invoiceLine.description}</td>
                  <td>{invoiceLine.quantity}</td>
                  <td>{invoiceLine.amount_egp}</td>
                  <td>{invoiceLine.sales_total}</td>
                  <td
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      handleDeleteInvoiceline(invoiceLine.id);
                    }}>
                    <Icon as={FaRegTrashAlt} color='blue' />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* offcanvas */}
          <div
            className={`offcanvas ${
              i18n.language === 'en' ? 'offcanvas-end' : 'offcanvas-start'
            } w-50`}
            tabIndex='-1'
            id='offcanvasExample'
            aria-labelledby='offcanvasExampleLabel'>
            <div
              className={`${
                i18n.language === 'en' ? 'justify-content-start' : 'justify-content-end'
              } offcanvas-header`}>
              <h5 className='offcanvas-title' id='offcanvasExampleLabel'>
                {isUpdateMode
                  ? t('stepper.invoiceLines.offcanvas.update')
                  : t('stepper.invoiceLines.offcanvas.add')}
              </h5>
              <button
                type='button'
                className='btn-close text-reset'
                data-bs-dismiss='offcanvas'
                aria-label='Close'></button>
            </div>
            <div className='offcanvas-body'>
              <form onSubmit={handleSaveInvoiceline} ref={refForm} autoComplete='off'>
                <div className='row justify-content-center mb-1'>
                  <select
                    id='itemType'
                    className='form-select col mx-2'
                    aria-label='Default select example'
                    name='item_type'
                    dir={`${i18n.language === 'ar' && 'rtl'}`}
                    onChange={handleChangeInvoiceLine}>
                    <option value=''>{t('stepper.invoiceLines.offcanvas.itemTypes.plain')}</option>
                    <option value='GS1'>GS1</option>
                    <option value='EGS'>EGS</option>
                  </select>
                  <select
                    id='itemCode'
                    name='item_code'
                    className='form-select col mx-2'
                    placeholder='Unique Number'
                    dir={`${i18n.language === 'ar' && 'rtl'}`}
                    onChange={handleChangeInvoiceLine}
                    onBlur={handleSelectItemCode}>
                    <option value=''>{t('stepper.invoiceLines.offcanvas.itemCodes.plain')}</option>
                    {itemCodes?.map(itemCode => (
                      <option key={itemCode.codeID} value={itemCode.codeLookupValue}>
                        {`${itemCode.codeLookupValue} (${itemCode.codeNamePrimaryLang})`}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='row justify-content-center'>
                  <input
                    id='internalCode'
                    name='internal_code'
                    type='text'
                    pattern='^[aA-zZ\d- ]+$'
                    title='Internal code should only contain letters and numbers. e.g. S001'
                    className='form-control col mx-2'
                    placeholder={t('stepper.invoiceLines.offcanvas.internalCode')}
                    value={itemCodeData ? itemCodeData.codeLookupValue.split('-').at(-1) : ''}
                    dir={`${i18n.language === 'ar' && 'rtl'}`}
                    disabled
                    autoComplete='off'
                  />
                  <textarea
                    id='description'
                    name='description'
                    type='text'
                    pattern='^[aA-zZ\d- ]+$'
                    title='Description should only contain letters and numbers. e.g. Organic Aria apples 750g'
                    className='form-control col mx-2'
                    placeholder={t('stepper.invoiceLines.offcanvas.description')}
                    defaultValue={itemCodeData ? itemCodeData.codeDescriptionPrimaryLang : ''}
                    dir={`${i18n.language === 'ar' && 'rtl'}`}
                    onChange={handleChangeInvoiceLine}
                    autoComplete='off'
                  />
                </div>
                <hr className='border border-dark border-1 opacity-50' />
                <div className='row justify-content-center mb-1'>
                  <input
                    id='itemQuantity'
                    name='quantity'
                    type='text'
                    pattern='^[\d.]+$'
                    title='Quantity should only contain numbers. e.g. 20.25'
                    className='form-control col mx-2'
                    placeholder={t('stepper.invoiceLines.offcanvas.quantity')}
                    defaultValue={isUpdateMode ? updatedQuantity : ''}
                    dir={`${i18n.language === 'ar' && 'rtl'}`}
                    onChange={handleChangeInvoiceLine}
                    onBlur={handleBlurQuantity}
                    required
                    autoComplete='off'
                  />
                  <select
                    id='unitType'
                    className='form-select col mx-2'
                    aria-label='Default select example'
                    name='unit_type'
                    dir={`${i18n.language === 'ar' && 'rtl'}`}
                    onChange={handleChangeInvoiceLine}>
                    <option value=''>{t('stepper.invoiceLines.offcanvas.unitType.plain')}</option>
                    {measureUnits?.map(measureUnit => (
                      <option key={measureUnit.id} value={measureUnit.id}>
                        {measureUnit.desc_en}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='row justify-content-center'>
                  <select
                    id='currencySold'
                    className='form-select col mx-2'
                    aria-label='Default select example'
                    name='currency'
                    dir={`${i18n.language === 'ar' && 'rtl'}`}
                    onChange={handleChangeInvoiceLine}>
                    <option value=''>{t('stepper.invoiceLines.offcanvas.currency.plain')}</option>
                    {currencies?.map(currency => (
                      <option key={currency.id} value={currency.id}>
                        {currency.desc_en}
                      </option>
                    ))}
                  </select>
                  <input
                    id='amountEGP'
                    name='amount_egp'
                    type='text'
                    pattern='^[\d.]+$'
                    title='Amount EGP should only contain numbers. e.g. 304.25'
                    className='form-control col mx-2'
                    placeholder={t('stepper.invoiceLines.offcanvas.amount')}
                    defaultValue={isUpdateMode ? updatedAmountEGP : ''}
                    dir={`${i18n.language === 'ar' && 'rtl'}`}
                    onChange={handleChangeInvoiceLine}
                    onBlur={handleBlurAmountEGP}
                    required
                    autoComplete='off'
                  />
                </div>
                <hr className='border border-dark border-1 opacity-50' />
                <div className='row justify-content-center mb-1'>
                  <input
                    id='salesTotal'
                    name='sales_total'
                    type='number'
                    className='form-control col mx-2'
                    placeholder={t('stepper.invoiceLines.offcanvas.salesTotal')}
                    dir={`${i18n.language === 'ar' && 'rtl'}`}
                    value={
                      isUpdateMode
                        ? +(updatedAmountEGP * updatedQuantity).toFixed(2)
                        : +(amountEGP * quantity).toFixed(2)
                    }
                    disabled
                  />
                </div>
                <div className='row justify-content-center'>
                  <input
                    id='rate'
                    name='discount_rate'
                    type='text'
                    pattern='^[\d.]+$'
                    title='Discount rate should only contain numbers. e.g. 1.5'
                    className='form-control col mx-2'
                    placeholder={t('stepper.invoiceLines.offcanvas.rate')}
                    defaultValue={isUpdateMode ? updatedDiscountRate : ''}
                    dir={`${i18n.language === 'ar' && 'rtl'}`}
                    onChange={handleChangeInvoiceLine}
                    onBlur={handleBlurDiscountRate}
                    autoComplete='off'
                  />
                  <input
                    id='amount'
                    name='discount_amount'
                    type='number'
                    className='form-control col mx-2'
                    placeholder={t('stepper.invoiceLines.offcanvas.discountAmount')}
                    dir={`${i18n.language === 'ar' && 'rtl'}`}
                    value={
                      isUpdateMode
                        ? +(
                            (updatedAmountEGP * updatedQuantity * updatedDiscountRate) /
                            100
                          ).toFixed(2)
                        : +((amountEGP * quantity * discountRate) / 100).toFixed(2)
                    }
                    disabled
                  />
                </div>
                <hr className='border border-dark border-1 opacity-50' />
                <h6 className='mb-2'>{t('stepper.invoiceLines.offcanvas.taxes.title')}</h6>

                <div
                  className={`${
                    i18n.language === 'en' ? 'row' : 'd-flex flex-row-reverse'
                  } justify-content-center text-center`}>
                  <table className='table col mx-2' dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
                    <thead
                      style={{
                        verticalAlign: 'middle',
                      }}>
                      <tr>
                        <th scope='col' className='col-4'>
                          {t('stepper.invoiceLines.offcanvas.taxes.taxType.column')}
                        </th>
                        <th scope='col' className='col-4'>
                          {t('stepper.invoiceLines.offcanvas.taxes.subType.column')}
                        </th>
                        <th scope='col' className='col-2'>
                          {t('stepper.invoiceLines.offcanvas.taxes.rate.column')}
                        </th>
                        <th scope='col' className='col-2'>
                          {t('stepper.invoiceLines.offcanvas.taxes.amount.column')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          <select
                            id='taxType'
                            className='form-select'
                            aria-label='Default select example'
                            name='tax_type'
                            dir={`${i18n.language === 'ar' && 'rtl'}`}
                            onChange={handleTaxType}
                            onBlur={handleChangeInvoiceLine}>
                            <option value=''>
                              {t('stepper.invoiceLines.offcanvas.taxes.taxType.plain')}
                            </option>
                            {taxables?.map(taxable => (
                              <option key={taxable.id} value={taxable.id}>
                                {`${taxable.code} : ${taxable.desc_ar}`}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <select
                            id='subType'
                            className='form-select'
                            aria-label='Default select example'
                            name='tax_subtype'
                            dir={`${i18n.language === 'ar' && 'rtl'}`}
                            onBlur={handleChangeInvoiceLine}>
                            <option value=''>
                              {t('stepper.invoiceLines.offcanvas.taxes.subType.plain')}
                            </option>
                            {taxlines?.flat()?.map(taxSubtype => (
                              <option key={taxSubtype.id} value={taxSubtype.id}>
                                {`${taxSubtype.code} : ${taxSubtype.desc_ar}`}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <input
                            id='rate'
                            name='tax_rate'
                            type='text'
                            pattern='^[\d.]+$'
                            title='Tax rate should only contain numbers. e.g. 14'
                            className='form-control'
                            placeholder={t('stepper.invoiceLines.offcanvas.taxes.rate.placeholder')}
                            defaultValue={isUpdateMode ? updatedTaxRate : ''}
                            dir={`${i18n.language === 'ar' && 'rtl'}`}
                            onChange={handleChangeInvoiceLine}
                            onBlur={handleBlurTaxRate}
                            required
                            autoComplete='off'
                          />
                        </td>
                        <td>
                          <input
                            id='amount'
                            name='tax_amount'
                            type='number'
                            className='form-control'
                            placeholder={t(
                              'stepper.invoiceLines.offcanvas.taxes.amount.placeholder'
                            )}
                            value={
                              isUpdateMode
                                ? +(
                                    ((updatedValueDifference +
                                      updatedAmountEGP *
                                        updatedQuantity *
                                        (1 - updatedDiscountRate / 100)) *
                                      updatedTaxRate) /
                                    100
                                  ).toFixed(2)
                                : +(
                                    ((valueDifference +
                                      amountEGP * quantity * (1 - discountRate / 100)) *
                                      withholdTaxFactor *
                                      taxRate) /
                                    100
                                  ).toFixed(2)
                            }
                            dir={`${i18n.language === 'ar' && 'rtl'}`}
                            disabled
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <hr className='border border-dark border-1 opacity-50' />
                <h6
                  className='mb-3 text-decoration-underline'
                  style={{ textUnderlineOffset: '10px' }}>
                  {t('stepper.invoiceLines.offcanvas.lineTotals.title')}
                </h6>
                <div className='row justify-content-center mb-2'>
                  {/* <div
                    className={`${
                      i18n.language === 'en' ? 'row' : 'd-flex flex-row-reverse'
                    } mb-1`}>
                    <h6 className='col-4 mt-2'>
                      {t('stepper.invoiceLines.offcanvas.lineTotals.totalTaxableFees')}
                    </h6>
                    <input
                      id='totalTaxableFees'
                      name='total_taxable_fees'
                      type='number'
                      className='form-control col'
                      placeholder={t('stepper.invoiceLines.offcanvas.lineTotals.totalTaxableFees')}
                      dir={`${i18n.language === 'ar' && 'rtl'}`}
                      disabled
                    />
                  </div> */}
                  <div
                    className={`${
                      i18n.language === 'en' ? 'row' : 'd-flex flex-row-reverse'
                    } mb-1`}>
                    <h6 className='col-4 mt-2'>
                      {t('stepper.invoiceLines.offcanvas.lineTotals.valueDifference')}
                    </h6>
                    <input
                      id='valueDifference'
                      name='value_difference'
                      type='text'
                      pattern='^[\d.]+$'
                      title='Value differnce should only contain numbers. e.g. 12.34'
                      className='form-control col'
                      placeholder={t('stepper.invoiceLines.offcanvas.lineTotals.valueDifference')}
                      defaultValue={isUpdateMode ? updatedValueDifference : ''}
                      dir={`${i18n.language === 'ar' && 'rtl'}`}
                      onChange={handleChangeInvoiceLine}
                      onBlur={handleBlurValueDifference}
                      autoComplete='off'
                    />
                  </div>
                  <div
                    className={`${
                      i18n.language === 'en' ? 'row' : 'd-flex flex-row-reverse'
                    } mb-1`}>
                    <h6 className='col-4 mt-2'>
                      {t('stepper.invoiceLines.offcanvas.lineTotals.itemsDiscount')}
                    </h6>
                    <input
                      id='itemsDiscount'
                      name='non_taxable_discount'
                      type='text'
                      pattern='^[\d.]+$'
                      title='Non taxable discount should only contain numbers. e.g. 20.25'
                      className='form-control col'
                      placeholder={t('stepper.invoiceLines.offcanvas.lineTotals.itemsDiscount')}
                      defaultValue={isUpdateMode ? updatedNonTaxableDiscount : ''}
                      dir={`${i18n.language === 'ar' && 'rtl'}`}
                      onChange={handleChangeInvoiceLine}
                      onBlur={handleBlurItemsDiscount}
                      autoComplete='off'
                    />
                  </div>
                  <div
                    className={`${
                      i18n.language === 'en' ? 'row' : 'd-flex flex-row-reverse'
                    } mb-1`}>
                    <h6 className='col-4 mt-2'>
                      {t('stepper.invoiceLines.offcanvas.lineTotals.netTotal')}
                    </h6>
                    <input
                      id='netTotal'
                      name='net_total'
                      type='number'
                      className='form-control col'
                      placeholder={t('stepper.invoiceLines.offcanvas.lineTotals.netTotal')}
                      value={
                        isUpdateMode
                          ? +(
                              updatedAmountEGP * updatedQuantity -
                              (updatedAmountEGP * updatedQuantity * updatedDiscountRate) / 100
                            ).toFixed(2)
                          : +(
                              amountEGP * quantity -
                              (amountEGP * quantity * discountRate) / 100
                            ).toFixed(2)
                      }
                      dir={`${i18n.language === 'ar' && 'rtl'}`}
                      disabled
                    />
                  </div>
                  <div className={`${i18n.language === 'en' ? 'row' : 'd-flex flex-row-reverse'}`}>
                    <h6 className='col-4 mt-2'>
                      {t('stepper.invoiceLines.offcanvas.lineTotals.total')}
                    </h6>
                    <input
                      id='total'
                      name='total'
                      type='number'
                      className='form-control col'
                      placeholder={t('stepper.invoiceLines.offcanvas.lineTotals.total')}
                      value={
                        isUpdateMode
                          ? +(
                              updatedAmountEGP * updatedQuantity -
                              (updatedAmountEGP * updatedQuantity * updatedDiscountRate) / 100 +
                              ((updatedValueDifference * updatedTaxRate) / 100 +
                                ((updatedAmountEGP * updatedQuantity -
                                  (updatedAmountEGP * updatedQuantity * updatedDiscountRate) /
                                    100) *
                                  updatedTaxRate) /
                                  100) -
                              updatedNonTaxableDiscount
                            ).toFixed(2)
                          : +(
                              amountEGP * quantity -
                              (amountEGP * quantity * discountRate) / 100 +
                              ((valueDifference * withholdTaxFactor * taxRate) / 100 +
                                ((amountEGP * quantity -
                                  (amountEGP * quantity * discountRate) / 100) *
                                  withholdTaxFactor *
                                  taxRate) /
                                  100) -
                              nonTaxableDiscount
                            ).toFixed(2)
                      }
                      dir={`${i18n.language === 'ar' && 'rtl'}`}
                      disabled
                    />
                  </div>
                </div>
                <div className='row'>
                  <button
                    onClick={isUpdateMode ? handleUpdateInvoiceline : handleSaveInvoiceline}
                    type='button'
                    className={`btn w-25 p-3 my-1 mx-auto ${
                      isUpdateMode ? 'btn-warning' : 'btn-success'
                    }`}
                    disabled={isUpdateMode ? false : Object.keys(invoicelines).length < 10}>
                    {isUpdateMode ? 'Update invoice line' : 'Save invoice line'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <div
        className={`${
          i18n.language === 'en' ? 'row' : 'd-flex flex-row-reverse'
        } justify-content-evenly fixed-bottom py-3 bg-secondary bg-gradient bg-opacity-10`}>
        <button
          className={`${
            i18n.language === 'ar' && 'offset-6'
          } button text-center col-1 bg-white text-dark border border-1 border-dark`}
          onClick={handleClose}>
          {t('stepper.footer.close')}
        </button>
        <button
          className={`${i18n.language === 'en' && 'offset-6'} button text-center col-1 ${
            step === 0 ? 'bg-secondary bg-gradient' : ''
          }`}
          onClick={handlePrevious}>
          {t('stepper.footer.previous')}
        </button>
        <button
          className={`button text-center col-1 ${
            invoiceLinesListLength < 1 ? 'bg-secondary bg-gradient' : ''
          }`}
          onClick={handleContinue}
          disabled={invoiceLinesListLength < 1}>
          {t('stepper.footer.continue')}
        </button>
      </div>
    </>
  );
}

export default InvoiceLines;
